import React from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx'; // You might need to install this: npm install clsx or yarn add clsx

const Button = ({ 
  children, 
  onClick, 
  variant = 'primary', 
  size = 'md', 
  disabled = false, 
  isLoading = false, // Added isLoading state
  className = '', 
  type = 'button', // Default type to button
  ...props 
}) => {
  const baseStyles = 'inline-flex items-center justify-center border font-medium rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 transition ease-in-out duration-150';

  // Using theme colors from tailwind.config.js
  const variantStyles = {
    primary: 'border-transparent text-white bg-blue-600 hover:bg-blue-700 focus:ring-primary-light', // Use specific blues, keep focus ring themed
    secondary: 'border-transparent text-white bg-secondary hover:bg-secondary-dark focus:ring-secondary-light', // Uses colors like #4B5563, #1F2937
    outline: 'border-gray-300 text-gray-700 bg-white hover:bg-gray-50 focus:ring-blue-500', // Standard outline
    danger: 'border-transparent text-white bg-red-600 hover:bg-red-700 focus:ring-red-500', // Example danger variant
    ghost: 'border-transparent text-gray-700 bg-transparent hover:bg-gray-100 focus:ring-blue-500', // Example ghost variant
    link: 'border-transparent bg-transparent text-blue-600 hover:text-blue-800 focus:outline-none focus:ring-0 shadow-none', // Minimal link style
    success: 'border-transparent text-white bg-green-600 hover:bg-green-700 focus:ring-green-500', // Add success variant
    // Add more variants as needed (e.g., 'link')
  };

  const sizeStyles = {
    sm: 'px-3 py-1.5 text-xs',
    md: 'px-2 py-1.5 text-sm',
    lg: 'px-6 py-3 text-base', // Adjusted padding for lg
    link: 'p-0' // No padding for link variant
  };

  const disabledStyles = 'opacity-50 cursor-not-allowed';
  const loadingStyles = 'cursor-wait'; // Style for loading state

  const buttonClasses = clsx(
    baseStyles,
    variantStyles[variant] || variantStyles.primary, // Default to primary if variant is invalid
    sizeStyles[size] && variant !== 'link' ? (sizeStyles[size] || sizeStyles.md) : '', 
    variant === 'link' && sizeStyles.link,
    (disabled || isLoading) && disabledStyles, // Apply disabled styles if disabled or loading
    isLoading && loadingStyles,
    variant === 'link' && 'shadow-none border-0',
    className // Allow passing additional custom classes
  );

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled || isLoading}
      className={buttonClasses}
      {...props}
    >
      {isLoading ? (
        // Simple loading spinner - consider a more visual one if needed
        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
      ) : null}
      {children}
    </button>
  );
};

Button.propTypes = {
  children: PropTypes.node.isRequired,
  onClick: PropTypes.func,
  variant: PropTypes.oneOf(['primary', 'secondary', 'outline', 'danger', 'ghost', 'link', 'success']),
  size: PropTypes.oneOf(['sm', 'md', 'lg', 'link']),
  disabled: PropTypes.bool,
  isLoading: PropTypes.bool,
  className: PropTypes.string,
  type: PropTypes.oneOf(['button', 'submit', 'reset']),
};

export default Button; 